import { withNamespaces } from "react-i18next";
import React, { useCallback, useMemo, useState } from "react";
import { Col, Container, Row, Progress, UncontrolledTooltip } from "reactstrap";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { withRouter, Link } from "react-router-dom";
import ControlService from "src/modules/data-protection/apis/ControlService";
import PageBody from "../../../../components/page-body";
import Navigator from "../../../../components/navigator";
import Select from 'react-select';
import Spinner from "react-bootstrap/Spinner";
import paginationFactory, {
    PaginationListStandalone,
    PaginationProvider,
    SizePerPageDropdownStandalone,
    PaginationTotalStandalone
} from "react-bootstrap-table2-paginator";
import BootstrapTable from "react-bootstrap-table-next";
import overlayFactory from "react-bootstrap-table2-overlay";
import FilteringComponent from "./filtering-component";
import { 
    EMPTY_LIST
} from "src/common/constants";
import "./style.scss";
import { formatTaskAndRiskId } from "src/modules/data-protection/constants";

const Controls = (props) => {
    const { t, lng } = props;

    const [tableFilters, setTableFilters] = useState({
        pageIndex: 1,
        pageSize: 25,
        sortField: null,
        sortOrder: null
    });

    const tableColumns = [
        {
            dataField: "id",
            text: t("ID"),
            sort: true,
            key: 0,
            style: {
                width: "60px"
            }
        },
        {
            key: 1,
            dataField: "title",
            text: props.t("Control Title"),
            sort: false,
            style: {
                width: "200px"
            },
            formatter: (cell, {
                id,
                title,
                description,
                controlNumber
            }) => {
                return (
                    <Link
                        to={`/admin/data-protection/controls/${id}/tasks`}
                    >
                        <span className="text-dark">
                            {controlNumber}. {title}
                        </span>

                        <i 
                            className="ri-information-line font-size-14 align-middle"
                            id={`control-${id}`}
                        >
                            <UncontrolledTooltip
                                target={`control-${id}`}
                                trigger="hover"
                            >
                                {description}
                            </UncontrolledTooltip>
                        </i>
                    </Link>
                );
            }
        },
        {
            dataField: "sectionTitle",
            text: t("Section"),
            sort: false,
            key: 2,
            style: {
                width: "120px"
            }
        },
        {
            dataField: "tasks",
            text: t("Progress"),
            sort: false,
            key: 3,
            style: {
                width: "300px",
            },
            formatter: (cell, {
                id,
                inProgressTasks,
                closedTasks,
                newTasks,
                deletedTasks,
                reviewTasks,
                totalTasks
            }) => {

                if(!totalTasks) {
                    return (
                        <span className="text-muted">
                            {t("No tasks available")}
                        </span>
                    )
                }

                const newTasksPercentage = Math.round(totalTasks > 0 ? (newTasks / totalTasks) * 100 : 0);
                const inProgressTasksPercentage = Math.round(totalTasks > 0 ? (inProgressTasks / totalTasks) * 100 : 0);
                const closedTasksPercentage = Math.round(totalTasks > 0 ? (closedTasks / totalTasks) * 100 : 0);
                const deletedTasksPercentage = Math.round(totalTasks > 0 ? (deletedTasks / totalTasks) * 100 : 0);
                const reviewTasksPercentage = Math.round(totalTasks > 0 ? (reviewTasks / totalTasks) * 100 : 0);

                return (    
                    <div>
                        <Progress 
                            multi
                            style={{
                                height: '12px'
                            }}
                        >
                            <Progress 
                                bar
                                barClassName="bg-task-new"
                                value={newTasksPercentage} 
                                id={`tooltip-new-tasks-${id}`}
                            >
                                <span className="text-dark">
                                    {newTasksPercentage}%
                                </span>
                                <UncontrolledTooltip 
                                    target={`tooltip-new-tasks-${id}`}
                                    placement="top"
                                >
                                    {t('New')}: {newTasks} {t('of')} {totalTasks}
                                </UncontrolledTooltip>
                            </Progress>
                            <Progress 
                                bar
                                barClassName="bg-task-in-progress"
                                value={inProgressTasksPercentage} 
                                id={`tooltip-in-progress-tasks-${id}`}
                            >
                                <span>
                                    {inProgressTasksPercentage}%
                                </span>
                                <UncontrolledTooltip 
                                    target={`tooltip-in-progress-tasks-${id}`}
                                    placement="top"
                                >
                                    {t('In Progress')}: {inProgressTasks} {t('of')} {totalTasks}
                                </UncontrolledTooltip>
                            </Progress>
                            <Progress 
                                bar
                                barClassName="bg-task-closed"
                                value={closedTasksPercentage} 
                                id={`tooltip-closed-tasks-${id}`}
                            >
                                <span
                                    className="text-dark"
                                >
                                    {closedTasksPercentage}%
                                </span>
                                <UncontrolledTooltip 
                                    target={`tooltip-closed-tasks-${id}`}
                                    placement="top"
                                >
                                    {t('Closed')}: {closedTasks} {t('of')} {totalTasks}
                                </UncontrolledTooltip>
                            </Progress>
                            <Progress 
                                bar
                                barClassName="bg-task-review"
                                value={reviewTasksPercentage} 
                                id={`tooltip-review-tasks-${id}`}
                            >
                                <span
                                    className="text-dark"
                                >
                                    {reviewTasksPercentage}%
                                </span>
                                <UncontrolledTooltip 
                                    target={`tooltip-review-tasks-${id}`}
                                    placement="top"
                                >
                                    {t('Review')}: {reviewTasks} {t('of')} {totalTasks}
                                </UncontrolledTooltip>
                            </Progress>
                            <Progress 
                                bar
                                barClassName="bg-task-delete"
                                value={deletedTasksPercentage} 
                                id={`tooltip-delete-tasks-${id}`}
                            >
                                <span
                                    className="text-dark"
                                >
                                    {deletedTasksPercentage}%
                                </span>
                                <UncontrolledTooltip 
                                    target={`tooltip-delete-tasks-${id}`}
                                    placement="top"
                                >
                                    {t('Deleted')}: {deletedTasks} {t('of')} {totalTasks}
                                </UncontrolledTooltip>
                            </Progress>
                        </Progress>
                    </div>
                );
            }   
        },
        {
            dataField: "tasks",
            text: t("Related Tasks"),
            sort: false,
            key: 4,
            style: {
                width: "200px",
            },
            formatter: (cell, {
                id: controlId
            }) => {
                return (
                    <div
                        style={{
                            display: 'inline-block',
                            width: '100%',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal'
                        }}
                    >
                        {(cell || []).map(({
                            id,
                            name,
                            asset_id,
                            case_secondary_id,
                            risk_secondary_id,
                            secondary_id,
                            is_mitigation,
                            questionnaire
                        }, index) => {
                            return (
                                <Link
                                    id={`control-${controlId}-task-${id}`}
                                    to={`/admin/data-protection/tasks/${id}/details?controlId=${controlId}`}
                                >
                                    {formatTaskAndRiskId(
                                        "task",
                                        questionnaire === 'software' ? asset_id : case_secondary_id,
                                        risk_secondary_id ?? secondary_id,
                                        is_mitigation,
                                        questionnaire,
                                        secondary_id
                                    )}
                                    <span 
                                        className="me-1"
                                        hidden={index === cell.length - 1}
                                    >
                                        {','}
                                    </span>

                                    <UncontrolledTooltip
                                        target={`control-${controlId}-task-${id}`}
                                        trigger="hover"
                                    >
                                        {name}
                                    </UncontrolledTooltip>
                                </Link>
                            )
                        })}
                    </div>
                )
            } 
        }
    ];

    const handleFetchControlsListQuery = useQuery({
        queryKey: [
            "data-protection-controls-list", 
            tableFilters,
            lng
        ],
        queryFn: async () => {
            const service = ControlService.getInstance();
            return await service.list(tableFilters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while retrieving the controls."), {
                type: "error"
            });
        },
    });

    const NoDataIndication = () =>
        handleFetchControlsListQuery.isFetched &&
            !handleFetchControlsListQuery.length ? (
            <div className="alert m-0" role="alert">
                <p
                    style={{
                        textAlign: "center",
                        marginBottom: 0,
                    }}
                >
                    {t(EMPTY_LIST)}
                </p>
            </div>
        ) : (
            <></>
        );

    const handleTableChange = (type, data) => {
        switch (type) {
            case "pagination":
                const { page, sizePerPage } = data
                setTableFilters({
                    ...tableFilters,
                    pageIndex: page,
                    pageSize: sizePerPage,
                });
                break;

            case 'sort': {
                const { sortField, sortOrder } = data
                setTableFilters({
                    ...tableFilters,
                    sortField: sortField,
                    sortOrder: sortOrder,
                });
                break
            }
            default:
                return false;
        }
    };

    const handleFilterChange = useCallback((newFilters) => {
        setTableFilters((currentFilters) => {
            return {
                ...currentFilters,
                ...newFilters
            }
        });
    }, []);

    const navigatorOptions = useMemo(() => {
        return {
            enable: true,
            breadCrumbs: [
                {
                    title: "SIP",
                    link: `/admin/data-protection/dashboard`,
                },
                {
                    title: "Controls",
                    link: `/admin/data-protection/controls`,
                }
            ]
        }
    }, []);

    return (
        <div className="page-content">
            <Container fluid>
                <Row>
                    <Col lg="12" className="mb-4">
                        <Navigator
                            {...navigatorOptions}
                            pageTitle={props.t("Controls")}
                        />
                    </Col>
                </Row>

                <PageBody>
                    <div className="p-4" style={{ marginBottom: "300px" }}>
                        <PaginationProvider
                            pagination={paginationFactory({
                                custom: true,
                                page: tableFilters.pageIndex,
                                sizePerPage: tableFilters.pageSize,
                                totalSize: parseInt(handleFetchControlsListQuery.data?.itemsCount || 0),
                                withFirstAndLast: false,
                                alwaysShowAllBtns: true,
                                prePageText: (
                                    <span>
                                        <i className="ri-arrow-left-s-line"></i> {t("Back")}
                                    </span>
                                ),
                                nextPageText: (
                                    <span>
                                        {t("Next")} <i className="ri-arrow-right-s-line"></i>
                                    </span>
                                ),
                                prePageTitle: t("Pre page"),
                                firstPageTitle: t("Next page"),
                                sizePerPageList: [
                                    {
                                        text: "25",
                                        value: 25,
                                    },
                                    {
                                        text: "50",
                                        value: 50,
                                    },
                                ],
                                showTotal: false,
                                paginationTotalRenderer: (from, to, size) => {
                                    return (
                                        <span className="react-bootstrap-table-pagination-total">
                                            {`${props.t('Total Items')} ${size}`}
                                        </span>
                                    )
                                },
                                hideSizePerPage: false,
                            })}
                        >
                            {({ paginationProps, paginationTableProps }) => (
                                <React.Fragment>
                                    <Row className="mb-5 mt-3">
                                        <Col sm="12">
                                            <div className="d-flex gap-2 justify-content-between">
                                                <Select
                                                    name='type'
                                                    classNamePrefix='select2-selection'
                                                    options={[{
                                                        value: 1,
                                                        label: 'ISO 27001:2022'
                                                    }]}
                                                    value={{
                                                        value: 1,
                                                        label: 'ISO 27001:2022'
                                                    }}
                                                    placeholder={t('Filter by Date')}
                                                    styles={{
                                                        container: (baseStyles) => ({
                                                            ...baseStyles,
                                                            width: '160px'
                                                        })
                                                    }}
                                                />

                                                <FilteringComponent
                                                    onChange={handleFilterChange}
                                                />
                                            </div>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <BootstrapTable
                                                remote={{
                                                    pagination: true,
                                                    filter: false,
                                                    sort: true,
                                                    cellEdit: false,
                                                    search: false,
                                                }}
                                                loading={
                                                    handleFetchControlsListQuery.isFetching ||
                                                    handleFetchControlsListQuery.isLoading
                                                }
                                                overlay={overlayFactory({
                                                    spinner: (
                                                        <Spinner
                                                            animation="border"
                                                            variant="primary"
                                                            size="md"
                                                        />
                                                    ),
                                                    text: "Loading...",
                                                })}
                                                onTableChange={handleTableChange}
                                                defaultSorted={[]}
                                                keyField={"id"}
                                                responsive
                                                bordered={false}
                                                data={handleFetchControlsListQuery.data?.controls || []}
                                                striped={false}
                                                columns={tableColumns}
                                                wrapperClasses="table-responsive"
                                                classes={"table tpdd-table"}
                                                headerWrapperClasses={"thead-light"}
                                                style={{
                                                    overflowX: "auto",
                                                }}
                                                noDataIndication={() => <NoDataIndication />}
                                                {...paginationTableProps}
                                            />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col sm="12">
                                            <div className="tpdd-pagination-style-1">
                                                <PaginationTotalStandalone
                                                    {...paginationProps}
                                                />

                                                <div>
                                                    <PaginationListStandalone
                                                        {...paginationProps}
                                                    />

                                                    <SizePerPageDropdownStandalone
                                                        {...paginationProps}
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </React.Fragment>
                            )}
                        </PaginationProvider>
                    </div>
                </PageBody>
            </Container>
        </div>
    );
};

export default withNamespaces()(
    withRouter(
        Controls
    )
);
